// TODO: Task 904718: refactor to use explicit dependency for es6 syntax rather than global variable
/* global Map */

import _ from 'underscore';
import Backbone from 'backbone';
// TODO [wewei] We already have code using the alpha.0 version of shadow-tree
// So we have to move
import { ShadowTree } from '@bingads-webui/shadow-tree';

// Utility function to generate a global unique child view ref
const uniqueRef = () => _.uniqueId('ref-');

// The tree of all views inheriting BaseView
const viewTree = new ShadowTree();

// A reverse map from HTMLElement to the view bound there
const elementMap = new Map();

const FORBIDDEN_OPTIONS = [
  // We don't allow people to pass in the view's root element on creation,
  // because the view would rely on an element without ownership. Either passing
  // a CSS selector or an HTMLElement would have the problem.
  // If the `el` is a CSS selector, the element may not exist, and the ownership
  // is uncertain.
  // If the `el` is an HTMLElement, the ownership is usually in the parent view.
  // And it maybe altered without notifying the this view.
  'el',
];

export const BaseView = Backbone.View.extend({
  // We cannot use ES6 `extends` here, because we need to use `this` before
  // calling `super(..args)`.
  // This is due to the limitation of `Backbone.View`. The constructor of
  // `Backbone.View` calls the `initialize` method, which is commonly used to
  // initialize the view instance. We need to ensure the view is registered in
  // the view tree when `initialize` is called, so that people can create and
  // connect the child views on initialization.
  // If we use ES6 `extends` grammar, it requires the `super` to be called
  // before using `this`. The `super()` will call `initialize`, so its called
  // before the view is registered. Calling `this.setChild(...)` in `initialize`
  // would trigger a runtime error.

  /**
   * An extension of `Backbone.View`.
   * It enhances the `Backbone.View` by maintaining the view hierarchy
   * Refer to the README.md for more comprehensive explanation.
   * @param {Object} options - The options for `Backbone.View`
   * @constructs BaseView
   * @return {BaseView} -
   */
  constructor: function constructor(options) {
    _.each(FORBIDDEN_OPTIONS, (name) => {
      if (_.has(options, name)) {
        throw new Error(`Option '${name}' is forbidden for BaseView`);
      }
    });

    viewTree.createNode(this, null, uniqueRef());

    try {
      Backbone.View.call(this, options);
    } catch (e) {
      viewTree.removeNode(this);
      throw e;
    }

    elementMap.set(this.el, this);
    /**
     * A view has been created
     * @event BaseViewEventEmitter#createdView
     * @type {BaseView}
     */
    BaseView.eventEmitter.trigger('createdView', this);
  },

  /**
   * Set the child view for given ref
   * @memberof BaseView#
   * @param {string} ref - The ref of the child view
   * @param {BaseView} view - The child view
   * @returns {BaseView} - The view itself
   */
  setChild(ref, view) {
    const { parent: parentOld, ref: refOld } = BaseView.getParentAndRef(view);

    if (parentOld) {
      throw new Error('View must be detached before attaching to a new parent');
    }

    if (this.getChild(ref)) {
      // The exiting child view under the ref is being replaced, it's no longer
      // reachable, so remove it
      this.detachChild(ref).remove();
    }

    viewTree.moveChild(null, refOld, this, ref);
    return this;
  },

  /**
   * Get the child view with the given ref
   * @memberof BaseView#
   * @param {string} ref - The ref of the child view
   * @returns {BaseView} - The child view
   */
  getChild(ref) {
    return BaseView.getChild(this, ref);
  },

  /**
   * Disconnect the child view with the given ref. This will make the child view
   * parent to `null`. This method WILL NOT remove the child view.
   * @memberof BaseView#
   * @param {string} ref - The ref of the child view to detach
   * @returns {BaseView} - The child view
   */
  detachChild(ref) {
    const child = this.getChild(ref);

    if (child) {
      viewTree.moveChild(this, ref, null, uniqueRef());
    }

    return child;
  },

  /**
   * Schedule to remove the view
   * @memberof BaseView#
   * @returns {void}
   */
  remove() {
    _.each(BaseView.getChildren(this), (child) => {
      child.remove();
      child.off();
      child.undelegateEvents();
    });

    viewTree.removeNode(this);
    elementMap.delete(this.el);
    Backbone.View.prototype.remove.call(this);
    /**
     * A view has been removed
     * @event BaseViewEventEmitter#removedView
     * @type {BaseView}
     */
    BaseView.eventEmitter.trigger('removedView', this);

    /**
     * This view has been removed
     * @event BaseView#removed
     * @type {void}
     */
    this.trigger('removed');
    this.off();
    this.undelegateEvents();
  },
}, {
  /**
   * @class BaseViewEventEmitter
   * @extends Backbone.Events
   */

  /**
   * The global event emitter.
   * The events exposed through the global event emitter should only be used
   * for debugging or building app level infrastructures (e.g. logging).
   * NOTE: DO NOT USE FOR FEATURE DEVELOPMENT.
   * @type {BaseViewEventEmitter}
   * @memberof BaseView
   */
  eventEmitter: _.extend({}, Backbone.Events),

  /**
   * Check if an object is a valid BaseView.
   * NOTE: DO NOT USE FOR FEATURE DEVELOPMENT.
   * @memberof BaseView
   * @param {BaseView} view - The view to validate
   * @returns {boolean} -
   *    True if the view is a BaseView and it's still in the view tree
   */
  isValid(view) {
    return (view instanceof BaseView) && viewTree.hasNode(view);
  },

  /**
   * Get the refs of all children
   * NOTE: DO NOT USE FOR FEATURE DEVELOPMENT.
   * @memberof BaseView
   * @param {BaseView} view - The parent view
   * @returns {string[]} - An array of ref strings
   */
  getChildrenRefs(view) {
    return viewTree.getChildrenRefs(view);
  },

  /**
   * Get all child views
   * NOTE: DO NOT USE FOR FEATURE DEVELOPMENT.
   * @memberof BaseView
   * @param {BaseView} view - The parent view
   * @returns {BaseView[]} - An array of child views
   */
  getChildren(view) {
    return viewTree.getChildren(view);
  },

  /**
   * Get the parent and ref of a view
   * NOTE: DO NOT USE FOR FEATURE DEVELOPMENT.
   * @memberof BaseView
   * @param {BaseView} view - The child view
   * @returns {{parent: BaseView, ref: string}} - The parent and the ref
   */
  getParentAndRef(view) {
    return viewTree.getParentAndRef(view);
  },

  /**
   * Get the parent of a view
   * NOTE: DO NOT USE FOR FEATURE DEVELOPMENT.
   * @memberof BaseView
   * @param {BaseView} view - The child view
   * @returns {BaseView} - The parent view
   */
  getParent(view) {
    return viewTree.getParentAndRef(view).parent;
  },

  /**
   * Get the ref of a view
   * NOTE: DO NOT USE FOR FEATURE DEVELOPMENT.
   * @memberof BaseView
   * @param {BaseView} view - The child view
   * @returns {string} - The ref of a view from its parent
   */
  getRef(view) {
    return viewTree.getParentAndRef(view).ref;
  },

  /**
   * Get the specified child view
   * NOTE: DO NOT USE FOR FEATURE DEVELOPMENT.
   * @memberof BaseView
   * @param {BaseView} view - The parent view
   * @param {string} ref - The ref for the child view
   * @returns {BaseView} - The child view
   */
  getChild(view, ref) {
    return viewTree.getChild(view, ref);
  },

  /**
   * Get the absolute path of a view
   * NOTE: DO NOT USE FOR FEATURE DEVELOPMENT.
   * @memberof BaseView
   * @param {BaseView} view - The issued view
   * @returns {string} - A path to locate a view following the refs
   */
  getPathOfView(view) {
    return viewTree.getPathOfItem(view);
  },

  /**
   * Query the view with a given path
   * NOTE: DO NOT USE FOR FEATURE DEVELOPMENT.
   * @memberof BaseView
   * @param {string} path - A path to locate a view following the refs
   * @returns {BaseView} - The view at the given path
   */
  getViewByPath(path) {
    return viewTree.getItemByPath(path);
  },

  /**
   * Lookup for the one and only view that directly includes the element. All
   * other views higher in the hierarchy can only include the element indirectly
   * through one of its descendant views
   * NOTE: DO NOT USE FOR FEATURE DEVELOPMENT.
   * @memberof BaseView
   * @param {Element} el - The DOM element
   * @returns {BaseView} - The smallest view covering the element
   */
  getViewForElement(el) {
    let elIter = el;

    while (elIter && !elementMap.has(elIter)) {
      elIter = elIter.parentNode;
    }

    return elementMap.get(elIter) || null;
  },

});
